import trace from "../trace.mjs";
import spinner from '../templates/spinner.html';
import { enterKeyDirective, focusDirective } from "../helpers/directives.mjs";

/**
 * Base Class for DialogBoxes to extend
 *
 * DialogBox is a pure data class designed to hold properties and methods associated
 * with a Vue template. Whilst in the DOM
 *
 * Because this class is referenced in the parameter declarations below, it needs
 * to come first. Jest seems to get a bit funny about hoisting in imports.
 *
 * @constructor
 * @property {DialogBoxManager} manager
 */
export class DialogBox {

    /**
     * Assign the dialog box to a manager
     * @param {DialogBoxManager} manager
     */
    setManager( manager ) {
        this.manager = manager;
    }

    /**
     * Assign the Vue component reference
     * @param {Vue} component
     */
    setComponent( component ) {
        this.component = component;
    }

    /**
     * Get the tag name for the Vue component
     *
     * Returns undefined if not configured in child class. Child classes should return a string
     * containing the tag name of the matching Vue component, without tags (i.e. literally just the
     * string used to name the component)
     *
     * @returns {string|undefined}
     */
    getComponentTag() {
        return undefined;
    }

    /**
     * Get computed data for the DialogBox
     *
     * This is intended to be overridden in derived classes. It provides data to the 'computed'
     * property of a Vue component, making it the ideal place to bind function calls to variables,
     * etc. You can also directly reference the DialogBox through model, but I think there's
     * some benefit to the Vue component knowing that a value is computed rather than directly
     * referencing a method on a static property.
     *
     * IMPORTANT: access these via data.{fieldname}
     *
     * @returns {{}}
     */
    getComponentData() {
        return {};
    }

    /**
     * Callback on value emission
     *
     * This is designed to be overridden in derived classes. The console warn
     * is to indicate DialogBoxes without handlers.
     *
     * @param values
     */
    onValueEmitted( ...values ) {
        console.warn('Unhandled emission:', this, values);
    }

    canCloseOverlay() {
        return true;
    }

    stack( new_dialog ) {
        trace.info( 'Dialog box: requesting stack', { self: this, requesting: new_dialog });
        this.manager.pushDialog( new_dialog );
    }

    /**
     *
     * @param {DialogBox} new_dialog
     */
    transitionTo( new_dialog, result ) {
        trace.info( 'Dialog box: requesting transition', { self: this, requesting: new_dialog });
        this.manager.removeDialog( this, result );
        this.manager.pushDialog( new_dialog );
    }

    close( result=null ) {
        trace.info( 'Dialog box: requesting close', { self: this, result: result });
        this.manager.removeDialog( this, result );
    }

    //
    // DialogBox hooks for overriding
    //
    onBeforeClose() {}
    onClose()       {}
}

/**
 * Emittable Values for DialogBoxes
 *
 * These are simply shortcut helpers, so we don't have to rely on string literals in the
 * main body of code.
 */
export const emittableValues = {
    OK     : 'ok',
    CANCEL : 'cancel',
    YES    : 'yes',
    NO     : 'no'
};

/**
 * Default computed properties for the DialogBox Vue template
 *
 * This is where we provide a link to the DialogBox class instance getComputedData() property,
 * meaning that we don't have to bind it in every single DialogBox, we can rely on this as a member
 * of the composite property object
 */
export const dialogDefaultComputed = {
    data : function() {
        /* We pass the internal update trigger so that Vue knows when to update this
         * computed value. Without it, the value stays cached and updateComputedData()
         * has no effect.
         */
        return this.model
            ? this.model.getComponentData( this.internalUpdateTrigger )
            : {} ;
    }
}

/**
 * Default static data to be provided to the dialog template
 *
 * This is where we include the emittable values to be used with the v-emit directive
 */
export const dialogDefaultData = {
    emitValues     : emittableValues,
    spinner        : spinner,

    // This last property is used in the jest tests to force an update of
    // the computed property 'data', which pulls data from the DialogBox
    // instance.
    internalUpdateTrigger : 1,
}

/**
 * Default directives for the dialog template
 *
 * This is where we bind directives to help with dialog box actions, for example the
 * emission of events on click. This is where we'd also bind more handlers (for example,
 * a keypress maybe?)
 */
export const dialogDefaultDirectives = {
    emit : {
        bind : function( element, binding, node ) {
            element.addEventListener( 'click', event => {
                node.context.emitValue( binding.value );
            });
        }
    },
    focus    : focusDirective,
    enterkey : enterKeyDirective
};

/**
 * Input properties for the dialog box Vue component
 *
 * This is where we provide the binding that allows us to tie the vue component to the DialogBox
 * handler class.
 *
 * IMPORTANT: model is the reference back to the DialogBox instance. You can use this property
 * to access methods, data, or properties inside it.
 *
 */
export const dialogDefaultProps = {
    model : DialogBox,
};

export const dialogDefaultWatch = {
    model : {
        // This means the handler is fired initially, rather than just for changes.
        immediate : true,
        // This handler helps maintain the link between component and model,
        // so that if a new model is presented, it sets the internal reference to the Vue component
        handler   : function( value, oldValue ) {
            // this test may be unnecessary?
            // does it trigger any watchers?
            // if not, maybe remove.
            if( value && value !== oldValue )
                value.setComponent( this );
        }
    }
}

/**
 * Methods to be available inside the DialogBox Vue template
 *
 * This is where we can bind any methods we want to be made available to the DialogBox Vue template
 */
export const dialogDefaultMethods = {
    close : function( result='' ) {
        this.model && this.model.close( result );
    },
    emitValue : function( ...values ) {
        this.$emit('dialog-emitter', this.model, ...values);
    },
    /**
     * Updates an internal property of the DialogBox
     *
     * During Jest Tests, jsdom doesn't update in the same way an actual browser does. We need
     * something inside the Vue data object that we can update in order to trigger a refresh
     * of the computed properties that hold references to it.
     *
     * @returns {Promise<void>}
     */
    updateComputedData : async function() {
        this.internalUpdateTrigger++;
        await this.$nextTick();
    }
};

/**
 * DEFAULT DIALOG OPTIONS COMPOSITE OBJECT
 *
 * This is the default template options object for a dialog box. It is designed to be overridden using
 * the spread operator where necessary, which also applies to individual properties. Example:
 * const x = new Vue({
 *     ...dialogDefaultConfiguration,
 *     methods: {
 *         ...dialogDefaultMethods,
 *         newMethod : function( value ) {},
 *     },
 *     template: '#template-name'
 * });
 */
export const dialogDefaultConfiguration = {
    computed   : dialogDefaultComputed,
    directives : dialogDefaultDirectives,
    methods    : dialogDefaultMethods,
    props      : dialogDefaultProps,
    watch      : dialogDefaultWatch,
    data       : () => dialogDefaultData,
};


